import { Injectable } from '@angular/core';

import { map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, EMPTY } from 'rxjs';

import { AuthService } from 'src/services/auth.service';
import { Constants } from 'src/constants';
import { LaunchDarklyService } from 'src/services/launchdarkly.service';

import { ExternalSessionService } from './external-session.service';

/**
 * Centralizes external mode state.
 * External mode supports users authenticated via Cognito (external partners)
 * rather than the internal COLA session, routing them to a separate UI and data layer.
 */
@Injectable({ providedIn: 'root' })
export class ExternalModeService {
	private isExternalModeSubject = new BehaviorSubject(this.resolveInitialExternalMode());

	public isExternalMode$ = this.isExternalModeSubject.asObservable();

	public isInternalUserWithExternalAccess = this.authService.userData$.pipe(
		map(userData => userData.functionalGroups?.some(group => group === Constants.GroupUids.EXTERNAL_ADMINISTRATORS) ?? false),
	);

	public hasExternalSession$ = this.externalSessionService.isLoggedIn$.pipe(
		tap(hasSession => {
			if (!hasSession) {
				this.isExternalModeSubject.next(false);
			}
		}),
	);

	public canStartExternalSession$ = combineLatest({
		hasAccess: this.isInternalUserWithExternalAccess,
		hasCurrentSession: this.hasExternalSession$,
	}).pipe(map(source => source.hasAccess && !source.hasCurrentSession));

	public canToggleExternalMode$ = combineLatest({
		hasSession: this.hasExternalSession$,
		isInternal: this.isInternalUserWithExternalAccess,
	}).pipe(map(source => source.hasSession && source.isInternal));

	constructor(
		private authService: AuthService,
		private externalSessionService: ExternalSessionService,
		private launchDarklyService: LaunchDarklyService,
	) {}

	/**
	 * Guards against a stale localStorage state where isExternalMode=true but the token has expired
	 * (e.g. user returns after a long absence without having logged out). Without this check, the app
	 * would try to make external API calls with an expired token and crash.
	 */
	private resolveInitialExternalMode(): boolean {
		if (localStorage.getItem('isExternalMode') !== 'true') return false;
		const token = localStorage.getItem('externalSessionToken');
		if (!token || this.externalSessionService.isTokenExpired(token)) {
			this.externalSessionService.clearSession();
			localStorage.removeItem('isExternalMode');
			return false;
		}
		return true;
	}

	public toggleExternalMode(): void {
		this.isExternalMode$
			.pipe(
				take(1),

				switchMap(isExternalMode => {
					if (!isExternalMode) {
						return this.externalSessionService.isLoggedIn$.pipe(
							take(1),
							switchMap(loggedIn => {
								if (!loggedIn) {
									return this.externalSessionService.login$();
								} else {
									this.switchMode(true);
									return EMPTY;
								}
							}),
						);
					} else {
						this.switchMode(false);
						return EMPTY;
					}
				}),
			)
			.subscribe();
	}

	private switchMode(external: boolean): void {
		if (external) {
			localStorage.setItem('isExternalMode', 'true');
		} else {
			localStorage.removeItem('isExternalMode');
		}
		this.isExternalModeSubject.next(external);
	}

	public activateExternalMode(): void {
		localStorage.setItem('isExternalMode', 'true');
		this.isExternalModeSubject.next(true);
	}
}
